import React from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  TextField,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export const ContactUsSection = () => {
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");

  const [errors, setErrors] = React.useState<{ [k: string]: string }>({});

  const underlineStyles = {
    "&:before": { borderBottom: "1px solid rgba(255,255,255,0.85)" },
    "&:hover:not(.Mui-disabled):before": { borderBottom: "2px solid white" },
    "&:after": { borderBottom: "2px solid white" },
  };

  const validate = () => {
    const next: { [k: string]: string } = {};
    if (!firstName.trim()) next.firstName = "First name is required.";
    if (!lastName.trim()) next.lastName = "Last name is required.";
    if (!email.trim()) next.email = "Email is required.";
    else if (!/^\S+@\S+\.\S+$/.test(email)) next.email = "Enter a valid email.";
    if (!message.trim()) next.message = "Please include a brief message.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    // ----------------- Will be sent to this email ----------------- //
    const to = "hughkevi@musc.edu";
    // -------------------------------------------------------------- //

    const subject = `Website Contact: ${firstName} ${lastName}`;
    const body = `Name: ${firstName} ${lastName}
Email: ${email}

Message:
${message}

-- 
Sent from HereditaryCancer.ai`;

    // Mailto: open the user's mail client with a prefilled draft
    window.location.href = `mailto:${encodeURIComponent(
      to
    )}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <Box
      id="contact-us"
      sx={{
        backgroundColor: "#1B2E85",
        color: "white",
        py: { xs: 6, md: 8 },
        px: 3,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ maxWidth: "700px", width: "100%" }}>
        {/* Header */}
        <Typography
          variant="h4"
          align="center"
          fontWeight="bold"
          mb={1.5}
          sx={{ fontSize: { xs: "2rem", md: "2.5rem" }, letterSpacing: 0.2 }}
        >
          Contact Us
        </Typography>
        <Typography
          align="center"
          sx={{ opacity: 0.9, mb: 5, maxWidth: 560, mx: "auto" }}
        >
          Have a question or want to explore collaboration? Reach out today—our
          team will follow up promptly.
        </Typography>

        {/* Form */}
        <form noValidate onSubmit={handleSubmit}>
          <Stack spacing={4}>
            {/* First + Last Name side-by-side on desktop */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={4}>
              <TextField
                label="First Name"
                fullWidth
                required
                variant="standard"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={Boolean(errors.firstName)}
                helperText={errors.firstName || " "}
                autoComplete="given-name"
                InputLabelProps={{ style: { color: "white" } }}
                InputProps={{
                  style: { color: "white" },
                  disableUnderline: false,
                  sx: underlineStyles,
                }}
              />
              <TextField
                label="Last Name"
                fullWidth
                required
                variant="standard"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName || " "}
                autoComplete="family-name"
                InputLabelProps={{ style: { color: "white" } }}
                InputProps={{
                  style: { color: "white" },
                  disableUnderline: false,
                  sx: underlineStyles,
                }}
              />
            </Stack>

            <TextField
              label="Email"
              fullWidth
              required
              variant="standard"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={Boolean(errors.email)}
              helperText={errors.email || " "}
              autoComplete="email"
              InputLabelProps={{ style: { color: "white" } }}
              InputProps={{
                style: { color: "white" },
                disableUnderline: false,
                sx: underlineStyles,
              }}
            />

            <TextField
              label="Message"
              fullWidth
              multiline
              rows={4}
              variant="standard"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              error={Boolean(errors.message)}
              helperText={errors.message || " "}
              InputLabelProps={{ style: { color: "white" } }}
              InputProps={{
                style: { color: "white" },
                disableUnderline: false,
                sx: underlineStyles,
              }}
            />

            {/* Disclaimer (good for professionalism in healthcare context) */}
            <Typography variant="caption" sx={{ opacity: 0.9, mt: -2 }}>
              Please don’t include personal medical details in this message.
            </Typography>

            {/* Submit Button */}
            <Box textAlign="center" mt={1}>
              <Button
                type="submit"
                variant="outlined"
                sx={{
                  color: "white",
                  borderColor: "white",
                  px: 5,
                  py: 1.5,
                  fontWeight: "bold",
                  textTransform: "none",
                  borderRadius: 999,
                  letterSpacing: 0.3,
                  "&:hover": {
                    backgroundColor: "white",
                    color: "#1B2E85",
                  },
                }}
              >
                Email Us
              </Button>
            </Box>
          </Stack>
        </form>

        {/* Footer Info Section */}
        <Box
          mt={8}
          sx={{
            borderTop: "1px solid rgba(255, 255, 255, 0.3)",
            pt: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* <Box
            display="flex"
            justifyContent="space-between"
            flexWrap="wrap"
            color="white"
            fontSize="1rem"
            sx={{ gap: 1 }}
          >
            <Typography>
              Address. Kevin S. Hughes, MD. 1 Vendue Range. Charleston, SC
              29401.
            </Typography>
            <Typography>Tel. PHONEGOESHERE</Typography>
            <Typography>
              Email.{" "}
              <a
                href="mailto:EMAILGOESHERE?subject=Website%20Inquiry"
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                EMAILGOESHERE
              </a>
            </Typography>
          </Box> */}

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={3}
          >
            <Typography fontSize="0.9rem" sx={{ opacity: 0.9 }}>
              © 2026 Kevin S. Hughes, MD. Sole Proprietor.
            </Typography>

            <IconButton
              onClick={handleScrollToTop}
              sx={{
                color: "white",
                border: "none",
                padding: 1,
                "&:hover": { transform: "scale(1.1)" },
              }}
              aria-label="Back to top"
            >
              <KeyboardArrowUpIcon fontSize="large" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
